import {
  type Analytics,
  getAnalytics,
  logEvent,
  setCurrentScreen,
  setUserId,
  setUserProperties
} from 'firebase/analytics'
import { initializeApp } from 'firebase/app'
import { getPerformance } from 'firebase/performance'
import moment from 'moment'
import ReactPixel from 'react-facebook-pixel'
import { v4 as uuidv4 } from 'uuid'
import type { ICity, ILocation } from '../stores/LocationStore'
import type { IUser } from '../stores/RegisterTypes'
import type { IBooking } from '../stores/bookingTypes'
/* eslint-disable max-params */
/* eslint-disable complexity */
import configurations from '../utils/config/configurations'
import type { Currency } from '../utils/currency'
import { postToApi } from './apiService'

function getCookie(name: string) {
  const match = document.cookie.match(RegExp(`(?:^|;\\s*)${name}=([^;]*)`))

  return match ? match[1] : null
}

function isMarketingConsentTrue() {
  const consentCookie = getCookie(`${process.env.REACT_APP_PLATFORM}.consent`)

  if (consentCookie) {
    const params = new URLSearchParams(consentCookie)

    return params.get('firebase') === 'true'
  }

  return false
}

let firebaseAnalytics: Analytics | null = null

const getFirebaseAnalytics = (): Analytics | null => {
  if (!isMarketingConsentTrue()) {
    return null
  }

  if (firebaseAnalytics) {
    return firebaseAnalytics
  }

  if (configurations.FIREBASE_CONFIG) {
    const app = initializeApp(configurations.FIREBASE_CONFIG)
    firebaseAnalytics = getAnalytics(app)
    const firebasePerformance = getPerformance()
    firebasePerformance.instrumentationEnabled = true
    firebasePerformance.dataCollectionEnabled = true

    return firebaseAnalytics
  }

  return null
}

let sessionId: string | null = null

const getSessionId = (): string => {
  if (!sessionId) {
    const storedSession = sessionStorage.getItem('booking_app_session')

    if (storedSession) {
      try {
        const session = JSON.parse(storedSession)
        const sessionExpiry = new Date(session.expiry)

        // Session valid if expiry is in the future
        if (sessionExpiry > new Date()) {
          sessionId = session.id
        }
      } catch (error) {
        console.error('Failed to parse stored session', error)
      }
    }

    if (!sessionId) {
      sessionId = uuidv4()
      const expiry = new Date(new Date().getTime() + 30 * 60000)
      sessionStorage.setItem(
        'booking_app_session',
        JSON.stringify({ id: sessionId, expiry: expiry })
      )
    }
  }

  return sessionId ?? ''
}

const sendEvent = async (
  eventName: 'click_info' | bookingEventType,
  params: any
) => {
  const fa = getFirebaseAnalytics()

  if (fa) {
    // Add session ID to all events
    params.session_id = getSessionId()
    logEvent(fa, eventName as string, params)
  }
}

export type bookingEventType =
  | 'book_step_1'
  | 'book_step_1_change_type'
  | 'book_step_1_search_location'
  | 'book_step_1_select_city'
  | 'book_step_1_select_location'
  | 'book_step_1_select_dates'
  | 'view_items_list'
  | 'select_item'
  | 'add_to_cart'
  | 'book_step_2_select_mileage'
  | 'book_step_2_select_protection'
  | 'book_step_2_select_delivery'
  | 'book_step_2_select_one_way_return_location_value'
  | 'begin_checkout'
  | 'add_payment_info'
  | 'purchase'
  | 'book_step_3_add_voucher';

export interface IBookingPropositionAnalytics {
  price: {
    currency: Currency;
    currencySymbol: string;
    amount: number;
    driveAmount: number;
    discountsAmount: number;
    voucherCode: string | null;
    discounts: { pricingCode: number; loyalty: number; partnerSource: number };
  };
  product: {
    reference: string;
  };
  location: {
    reference: string;
  };
  vehicleModel: {
    reference: string;
  };
  schedule: {
    startAt: string;
    endAt: string;
    duration: number;
  };
  options: {
    price: {
      value: number;
      currency: string;
    };
    mileage: {
      value: number;
      unit: string;
    };
    insurance: {
      value: string;
      price: number;
    };
    deliveries: {
      value: string;
    };
    generics: Array<{
      options: {
        reference: string;
      };
    }>;
    oneWay: {
      value: string;
    };
  };
  loyaltyProgram: {
    selectedLoyaltyPrograms: Array<{
      loyaltyPrograms: {
        reference: string;
      };
    }>;
  };
  alternative: {
    reference: string;
    type: string;
  };
}

/**
 * Converts an IBooking object to BookingPropositionAnalytics format for analytics
 */
const convertBookingToAnalytics = (
  booking: IBooking
): IBookingPropositionAnalytics => {
  return {
    price: {
      currency: booking.price.currency,
      currencySymbol: booking.price.currencySymbol || booking.price.currency,
      amount: booking.price.amount,
      driveAmount: booking.price.amount || 0,
      discountsAmount: booking.price.discountsAmount || 0,
      voucherCode: booking.price.voucherCode || null,
      discounts: {
        pricingCode: (booking.price.discounts)?.pricingCode || 0,
        loyalty: (booking.price.discounts)?.loyalty || 0,
        partnerSource: (booking.price.discounts)?.partnerSource || 0
      }
    },
    product: { reference: booking.product.reference },
    location: { reference: booking.location.reference },
    vehicleModel: { reference: booking.vehicleModel.reference },
    schedule: {
      startAt: booking.schedule.startAt,
      endAt: booking.schedule.endAt,
      duration: booking.schedule.duration
    },
    options: {
      price: {
        value: booking.price.amount,
        currency: booking.price.currency
      },
      mileage: {
        value: booking.options?.mileage?.value || 0,
        unit: ''
      },
      insurance: {
        value: booking.options?.insurances?.value || '',
        price: 0
      },
      deliveries: { value: booking.options?.deliveries?.price?.amount?.toString() || '0' },
      generics: [],
      oneWay: { value: booking.options?.oneWay?.value || '' }
    },
    loyaltyProgram: { selectedLoyaltyPrograms: []},
    alternative: {
      reference: booking.alternative?.reference || '',
      type: booking.alternative?.type || ''
    }
  }
}

export const sendBookingAnalyticEvent = async (
  eventName: bookingEventType,
  selectedCity?: ICity | null,
  selectedLocation?: ILocation | null,
  proposedBookings?: IBooking[] | null,
  _selectedBookingIndex?: number | null,
  selectedBooking?: IBooking | null,
  searchField?: string | null,
  searchResult?: number | null
) => {
  try {
    console.debug('analyticsHelper.sendBookingAnalyticEvent', eventName)

    // Convert IBooking objects to BookingPropositionAnalytics
    const analyticsSelectedBooking = selectedBooking
      ? convertBookingToAnalytics(selectedBooking)
      : null

    const analyticsProposedBookings = proposedBookings
      ? proposedBookings.map((booking) => convertBookingToAnalytics(booking))
      : null

    const params: any = {
      instance_brand: configurations.UFO_APP_PLATFORM,
      location_search_typed: searchField || undefined,
      location_search_results: searchResult === 0 ? 0 : searchResult || undefined,
      location_selected_country: selectedCity?.country?.code || undefined,
      location_selected_city: selectedCity?.reference || undefined,
      location_selected_location: selectedLocation?.reference || undefined,
      transaction_id: (selectedBooking as any)?.reference,
      payment_type: ['purchase', 'add_payment_info'].includes(eventName)
        ? 'credit_card'
        : undefined,
      booking_propositions: analyticsProposedBookings,
      selected_booking_proposition: analyticsSelectedBooking,
      session_id: getSessionId(),
      booking_event: eventName
    }

    if (!selectedBooking && proposedBookings && proposedBookings[0]) {
      params.currency = proposedBookings[0].price.currency
    }


    sendEvent(eventName, params)
    await postToApi('/v2/booking_analytics', params, true)
  } catch (error) {
    console.error(error)
  }
}

export const addAnalyticsEvent = async (eventName: string, params: any) => {
  try {
    console.debug('analyticsHelper.addAnalyticsEvent', eventName)

    if (
      process.env.REACT_APP_ENV === 'production' &&
      process.env.REACT_APP_PLATFORM === 'ufodrive'
    ) {
      ReactPixel.trackCustom(eventName, params)
    }
  } catch (error) {
    console.error('analyticsHelper.addAnalyticsEvent', error)
  }
}

export const initialiseAnalyticsUser = async (
  user: IUser,
  partnerCode?: string
): Promise<void> => {
  try {
    const fa = getFirebaseAnalytics()

    if (!fa) {
      return
    }

    console.debug('analyticsHelper.initialiseAnalyticsUser', user.reference)

    setUserId(fa, user.reference)
    setUserProperties(fa, {
      role: user.roleReference ||
        (user as any)?.role?.reference ||
        (user as any)?.role,
      status: user.status,
      partnerCode
    })

    if (
      process.env.REACT_APP_PLATFORM === 'uft' ||
      process.env.REACT_APP_PLATFORM === 'ufodrive'
    ) {
      const options = {
        autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
        debug: false // enable logs
      }
      ReactPixel.init('658866822292578', undefined, options)
      addAnalyticsEvent('/booking/book', {})
    }
  } catch (error) {
    console.error('analyticHelper.initialiseAnalyticsUser', error)
  }
}

export const sendDescriptionEvent = async (
  eventName: 'click_info',
  descriptionReference: string
) => {
  try {
    console.debug('analyticsHelper.sendDescriptionEvent', eventName)
    const params: any = {
      instance_brand: configurations.UFO_APP_PLATFORM,
      info: descriptionReference
    }

    sendEvent(eventName, params)
  } catch (error) {
    console.error(error)
  }
}

export const viewAnalyticsScreen = async (screenName: string) => {
  try {
    const fa = getFirebaseAnalytics()

    if (!fa) {
      return
    }

    console.debug('analyticsHelper.viewAnalyticsScreen', screenName)
    setCurrentScreen(fa, screenName)
    const performedAt = moment().toDate()
    const payload = {
      severity: 'info',
      code: '0',
      action: 'Navigation/NAVIGATE',
      message: screenName,
      performed_at: performedAt
    }
    postToApi('/v2/user_experiences', payload, true)

    if (
      process.env.REACT_APP_ENV === 'production' &&
      process.env.REACT_APP_PLATFORM === 'ufodrive'
    ) {
      ReactPixel.track('ViewContent', { content_name: screenName })
    }
  } catch (error) {
    console.error('analyticsHelper.viewAnalyticsScreen', error)
  }
}
